import { BaseApiClient } from './base-client'
import {
  type ApiResponse,
  type KnowledgeBase,
  type KnowledgeBaseRelationshipType,
  type Link,
  type Listing,
  type ListingUrlSyncConfig,
  type Pronunciation,
  type UpdateLinkDto,
  type CreatePronunciationDto,
  type UpdatePronunciationDto,
  API_ROUTES,
  interpolateRoute,
} from '@super/shared-types'
import { withErrorHandling } from '../utils'

export type RelationshipType = KnowledgeBaseRelationshipType

export interface CreateKnowledgeBaseRequest {
  title: string
  body: string
  propertyIds?: string[]
  propertyGroupIds?: string[]
  relationshipTypes?: RelationshipType[]
}

export interface UpdateKnowledgeBaseRequest {
  title?: string
  body?: string
  propertyIds?: string[]
  propertyGroupIds?: string[]
  relationshipTypes?: RelationshipType[]
}

export class KnowledgeApiClient extends BaseApiClient {
  /**
   * Get all knowledge bases
   * @returns KnowledgeBase[]
   */
  async getKnowledgeBases(): Promise<KnowledgeBase[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase[]>>(
          API_ROUTES.KNOWLEDGE_BASES.GET_ALL,
        )
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching knowledge bases',
      },
    )
    return result || []
  }

  /**
   * Get a specific knowledge base by ID
   * @param id - The ID of the knowledge base to get
   * @returns KnowledgeBase | null
   */
  async getKnowledgeBase(id: string): Promise<KnowledgeBase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase>>(
          interpolateRoute(API_ROUTES.KNOWLEDGE_BASES.GET, { id }),
        )
        return response.data || null
      },
      {
        on404: 'ignore',
        errorContext: 'Error fetching knowledge base',
      },
    )
  }

  /**
   * Create a new knowledge base
   * @param data - The data for the new knowledge base
   * @returns KnowledgeBase | null
   */
  async createKnowledgeBase(
    data: CreateKnowledgeBaseRequest,
  ): Promise<KnowledgeBase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase>>(
          API_ROUTES.KNOWLEDGE_BASES.CREATE,
          {
            method: 'POST',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating knowledge base',
      },
    )
  }

  /**
   * Update an existing knowledge base
   * @param id - The ID of the knowledge base to update
   * @param data - The data to update the knowledge base with
   * @returns KnowledgeBase | null
   */
  async updateKnowledgeBase(
    id: string,
    data: UpdateKnowledgeBaseRequest,
  ): Promise<KnowledgeBase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase>>(
          interpolateRoute(API_ROUTES.KNOWLEDGE_BASES.UPDATE, { id }),
          {
            method: 'PUT',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating knowledge base',
      },
    )
  }

  /**
   * Delete a knowledge base
   * @param id - The ID of the knowledge base to delete
   * @returns { success: boolean; message: string } | null
   */
  async deleteKnowledgeBase(
    id: string,
  ): Promise<{ success: boolean; message: string } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{ success: boolean; message: string }>
        >(interpolateRoute(API_ROUTES.KNOWLEDGE_BASES.DELETE, { id }), {
          method: 'DELETE',
        })
        return response.data || { success: false, message: 'Delete failed' }
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error deleting knowledge base',
      },
    )
  }

  /**
   * Get knowledge bases for a specific company (Super Admin only)
   * @param companyId - The ID of the company to get knowledge bases for
   * @returns KnowledgeBase[]
   */
  async getKnowledgeBasesForCompany(
    companyId: string,
  ): Promise<KnowledgeBase[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase[]>>(
          `${API_ROUTES.ADMIN.KNOWLEDGE_BASES.GET_ALL}?companyId=${companyId}`,
        )
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching knowledge bases for company',
      },
    )
    return result || []
  }

  /**
   * Create knowledge base for a specific company (Super Admin only)
   * @param companyId - The ID of the company to create the knowledge base for
   * @param data - The data for the new knowledge base
   * @returns KnowledgeBase | null
   */
  async createKnowledgeBaseForCompany(
    companyId: string,
    data: CreateKnowledgeBaseRequest,
  ): Promise<KnowledgeBase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase>>(
          `${API_ROUTES.ADMIN.KNOWLEDGE_BASES.CREATE}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating knowledge base for company',
      },
    )
  }

  /**
   * Update knowledge base for a specific company (Super Admin only)
   * @param id - The ID of the knowledge base to update
   * @param companyId - The ID of the company to update the knowledge base for
   * @param data - The data to update the knowledge base with
   * @returns KnowledgeBase | null
   */
  async updateKnowledgeBaseForCompany(
    id: string,
    companyId: string,
    data: UpdateKnowledgeBaseRequest,
  ): Promise<KnowledgeBase | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<KnowledgeBase>>(
          `${interpolateRoute(API_ROUTES.ADMIN.KNOWLEDGE_BASES.UPDATE, { id })}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating knowledge base for company',
      },
    )
  }

  /**
   * Delete knowledge base for a specific company (Super Admin only)
   * @param id - The ID of the knowledge base to delete
   * @param companyId - The ID of the company to delete the knowledge base for
   * @returns { success: boolean; message: string } | null
   */
  async deleteKnowledgeBaseForCompany(
    id: string,
    companyId: string,
  ): Promise<{ success: boolean; message: string } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{ success: boolean; message: string }>
        >(
          `${interpolateRoute(API_ROUTES.ADMIN.KNOWLEDGE_BASES.DELETE, { id })}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
        return response.data || { success: false, message: 'Delete failed' }
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error deleting knowledge base for company',
      },
    )
  }

  /**
   * Update a link
   * @param id - The ID of the link to update
   * @param data - The data to update the link with
   * @returns Link | null
   */
  async updateLink(id: string, data: UpdateLinkDto): Promise<Link | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Link>>(
          interpolateRoute(API_ROUTES.KNOWLEDGE_BASES.LINKS.UPDATE, { id }),
          {
            method: 'PUT',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating link',
      },
    )
  }

  /**
   * Update a link for a specific company (Super Admin only)
   * @param id - The ID of the link to update
   * @param companyId - The ID of the company
   * @param data - The data to update the link with
   * @returns Link | null
   */
  async updateLinkForCompany(
    id: string,
    companyId: string,
    data: UpdateLinkDto,
  ): Promise<Link | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Link>>(
          `${interpolateRoute(API_ROUTES.ADMIN.KNOWLEDGE_BASES.LINKS.UPDATE, { id })}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating link for company',
      },
    )
  }

  /**
   * Get all links for the current company
   * @returns Link[]
   */
  async getLinks(): Promise<Link[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Link[]>>(
          API_ROUTES.KNOWLEDGE_BASES.LINKS.GET_ALL,
        )
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching links',
      },
    )
    return result || []
  }

  /**
   * Get all links for a specific company (Super Admin only)
   * @param companyId - The ID of the company
   * @returns Link[]
   */
  async getAdminLinksForCompany(companyId: string): Promise<Link[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Link[]>>(
          `${API_ROUTES.ADMIN.KNOWLEDGE_BASES.LINKS.GET_ALL}?companyId=${companyId}`,
        )
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching links for company',
      },
    )
    return result || []
  }

  /**
   * Get all pronunciations for the current company
   * @returns Pronunciation[]
   */
  async getPronunciations(): Promise<Pronunciation[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Pronunciation[]>>(
          API_ROUTES.KNOWLEDGE_BASES.PRONUNCIATIONS.GET_ALL,
        )
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching pronunciations',
      },
    )
    return result || []
  }

  /**
   * Get all pronunciations for a specific company (Super Admin only)
   * @param companyId - The ID of the company
   * @returns Pronunciation[]
   */
  async getAdminPronunciationsForCompany(
    companyId: string,
  ): Promise<Pronunciation[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Pronunciation[]>>(
          `${API_ROUTES.ADMIN.KNOWLEDGE_BASES.PRONUNCIATIONS.GET_ALL}?companyId=${companyId}`,
        )
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching pronunciations for company',
      },
    )
    return result || []
  }

  /**
   * Create a pronunciation
   * @param data - The data for the new pronunciation
   * @returns Pronunciation | null
   */
  async createPronunciation(
    data: CreatePronunciationDto,
  ): Promise<Pronunciation | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Pronunciation>>(
          API_ROUTES.KNOWLEDGE_BASES.PRONUNCIATIONS.CREATE,
          {
            method: 'POST',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating pronunciation',
      },
    )
  }

  /**
   * Create a pronunciation for a specific company (Super Admin only)
   * @param companyId - The ID of the company
   * @param data - The data for the new pronunciation
   * @returns Pronunciation | null
   */
  async createPronunciationForCompany(
    companyId: string,
    data: CreatePronunciationDto,
  ): Promise<Pronunciation | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Pronunciation>>(
          `${API_ROUTES.ADMIN.KNOWLEDGE_BASES.PRONUNCIATIONS.CREATE}?companyId=${companyId}`,
          {
            method: 'POST',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating pronunciation for company',
      },
    )
  }

  /**
   * Update a pronunciation
   * @param id - The ID of the pronunciation to update
   * @param data - The data to update the pronunciation with
   * @returns Pronunciation | null
   */
  async updatePronunciation(
    id: string,
    data: UpdatePronunciationDto,
  ): Promise<Pronunciation | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Pronunciation>>(
          interpolateRoute(API_ROUTES.KNOWLEDGE_BASES.PRONUNCIATIONS.UPDATE, {
            id,
          }),
          {
            method: 'PUT',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating pronunciation',
      },
    )
  }

  /**
   * Update a pronunciation for a specific company (Super Admin only)
   * @param id - The ID of the pronunciation to update
   * @param companyId - The ID of the company
   * @param data - The data to update the pronunciation with
   * @returns Pronunciation | null
   */
  async updatePronunciationForCompany(
    id: string,
    companyId: string,
    data: UpdatePronunciationDto,
  ): Promise<Pronunciation | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Pronunciation>>(
          `${interpolateRoute(API_ROUTES.ADMIN.KNOWLEDGE_BASES.PRONUNCIATIONS.UPDATE, { id })}?companyId=${companyId}`,
          {
            method: 'PUT',
            body: JSON.stringify(data),
          },
        )
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error updating pronunciation for company',
      },
    )
  }

  /**
   * Delete a pronunciation
   * @param id - The ID of the pronunciation to delete
   * @returns { success: boolean; message: string } | null
   */
  async deletePronunciation(
    id: string,
  ): Promise<{ success: boolean; message: string } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{ success: boolean; message: string }>
        >(
          interpolateRoute(API_ROUTES.KNOWLEDGE_BASES.PRONUNCIATIONS.DELETE, {
            id,
          }),
          {
            method: 'DELETE',
          },
        )
        return response.data || { success: false, message: 'Delete failed' }
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error deleting pronunciation',
      },
    )
  }

  /**
   * Delete a pronunciation for a specific company (Super Admin only)
   * @param id - The ID of the pronunciation to delete
   * @param companyId - The ID of the company
   * @returns { success: boolean; message: string } | null
   */
  async deletePronunciationForCompany(
    id: string,
    companyId: string,
  ): Promise<{ success: boolean; message: string } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{ success: boolean; message: string }>
        >(
          `${interpolateRoute(API_ROUTES.ADMIN.KNOWLEDGE_BASES.PRONUNCIATIONS.DELETE, { id })}?companyId=${companyId}`,
          {
            method: 'DELETE',
          },
        )
        return response.data || { success: false, message: 'Delete failed' }
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error deleting pronunciation for company',
      },
    )
  }

  /**
   * Get all listings for the current company
   * @returns Listing[]
   */
  async getListings(): Promise<Listing[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Listing[]>>(
          API_ROUTES.LISTINGS.GET_ALL,
        )
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching listings',
      },
    )
    return result || []
  }

  /**
   * Get all listings for a specific company (Super Admin only)
   * @param companyId - The ID of the company
   * @returns Listing[]
   */
  async getAdminListingsForCompany(companyId: string): Promise<Listing[]> {
    const result = await withErrorHandling(
      async () => {
        const response = await this.request<ApiResponse<Listing[]>>(
          `${API_ROUTES.ADMIN.LISTINGS.GET_ALL}?companyId=${companyId}`,
        )
        return response.data || []
      },
      {
        onApiError: 'log-and-return-null',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching listings for company',
      },
    )
    return result || []
  }

  /**
   * Scrape listings from a URL
   * @param url - The URL to sync listings from
   * @param mode - The scrape mode (delete, append, update)
   * @returns { success: boolean; jobId: string; url: string; mode: string; messageId: string; message: string } | null
   */
  async scrapeListings(
    url: string,
    mode: 'delete' | 'append' | 'update',
  ): Promise<{
    success: boolean
    jobId: string
    url: string
    mode: string
    messageId: string
    message: string
  } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{
            success: boolean
            jobId: string
            url: string
            mode: string
            messageId: string
            message: string
          }>
        >(API_ROUTES.LISTINGS.SCRAPE, {
          method: 'POST',
          body: JSON.stringify({ url, mode }),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error syncing listings',
      },
    )
  }

  /**
   * Scrape listings from a URL for a specific company (Super Admin only)
   * @param companyId - The ID of the company
   * @param url - The URL to sync listings from
   * @param mode - The scrape mode (delete, append, update)
   * @returns { success: boolean; jobId: string; url: string; mode: string; companyId: string; messageId: string; message: string } | null
   */
  async scrapeListingsForCompany(
    companyId: string,
    url: string,
    mode: 'delete' | 'append' | 'update',
  ): Promise<{
    success: boolean
    jobId: string
    url: string
    mode: string
    companyId: string
    messageId: string
    message: string
  } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{
            success: boolean
            jobId: string
            url: string
            mode: string
            companyId: string
            messageId: string
            message: string
          }>
        >(`${API_ROUTES.ADMIN.LISTINGS.SCRAPE}?companyId=${companyId}`, {
          method: 'POST',
          body: JSON.stringify({ url, mode }),
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error syncing listings for company',
      },
    )
  }

  /**
   * Get listing URL sync status
   */
  async getListingUrlSyncStatus(): Promise<ListingUrlSyncConfig | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{
            success: boolean
            data: ListingUrlSyncConfig | null
          }>
        >(API_ROUTES.LISTINGS.SYNC_URL_STATUS, {
          method: 'GET',
        })
        // API returns { data: { success: true, data: ListingUrlSyncConfig | null } }
        // Extract the inner data property
        return response.data?.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching listing URL sync status',
      },
    )
  }

  /**
   * Get listing URL sync status for a specific company (Super Admin only)
   * @param companyId - The ID of the company
   */
  async getAdminListingUrlSyncStatus(
    companyId: string,
  ): Promise<ListingUrlSyncConfig | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{
            success: boolean
            data: ListingUrlSyncConfig | null
          }>
        >(
          `${API_ROUTES.ADMIN.LISTINGS.SYNC_URL_STATUS}?companyId=${companyId}`,
          {
            method: 'GET',
          },
        )
        return response.data?.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error fetching listing URL sync status for company',
      },
    )
  }

  /**
   * Create or update listing URL sync
   */
  async createOrUpdateListingUrlSync(
    syncUrl: string,
    mode: 'delete' | 'append' | 'update' = 'delete',
  ): Promise<{
    success: boolean
    data: ListingUrlSyncConfig
    message: string
  } | null> {
    return withErrorHandling(
      async () => {
        // ObservabilityInterceptor wraps response as { data: extractedData, message, timestamp, correlationId }
        // Controller returns { success: true, data: syncConfig, message: '...' }
        // Interceptor extracts inner data, so response.data is the syncConfig object directly
        const response = await this.request<
          ApiResponse<ListingUrlSyncConfig> & {
            message?: string
          }
        >(API_ROUTES.LISTINGS.SYNC_URL, {
          method: 'POST',
          body: JSON.stringify({
            syncUrl,
            mode,
          }),
        })

        // response.data is the syncConfig object (extracted by interceptor)
        // response.message is the message string
        if (response.data) {
          return {
            success: true,
            data: response.data,
            message:
              response.message || 'Listing URL sync configured successfully',
          }
        }

        return null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating/updating listing URL sync',
      },
    )
  }

  /**
   * Create or update listing URL sync for a specific company (Super Admin only)
   * @param companyId - The ID of the company
   * @param syncUrl - The URL to sync listings from
   * @param mode - The sync mode (delete, append, update)
   */
  async createOrUpdateAdminListingUrlSync(
    companyId: string,
    syncUrl: string,
    mode: 'delete' | 'append' | 'update' = 'delete',
  ): Promise<{
    success: boolean
    data: ListingUrlSyncConfig
    message: string
  } | null> {
    return withErrorHandling(
      async () => {
        // ObservabilityInterceptor wraps response as { data: extractedData, message, timestamp, correlationId }
        // Controller returns { success: true, data: syncConfig, message: '...' }
        // Interceptor extracts inner data, so response.data is the syncConfig object directly
        const response = await this.request<
          ApiResponse<ListingUrlSyncConfig> & {
            message?: string
          }
        >(`${API_ROUTES.ADMIN.LISTINGS.SYNC_URL}?companyId=${companyId}`, {
          method: 'POST',
          body: JSON.stringify({
            syncUrl,
            mode,
          }),
        })

        // response.data is the syncConfig object (extracted by interceptor)
        // response.message is the message string
        if (response.data) {
          return {
            success: true,
            data: response.data,
            message:
              response.message || 'Listing URL sync configured successfully',
          }
        }

        return null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error creating/updating listing URL sync for company',
      },
    )
  }

  /**
   * Remove listing URL sync
   */
  async removeListingUrlSync(): Promise<{
    success: boolean
    message: string
  } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{
            success: boolean
            message: string
          }>
        >(API_ROUTES.LISTINGS.SYNC_URL, {
          method: 'DELETE',
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error removing listing URL sync',
      },
    )
  }

  /**
   * Remove listing URL sync for a specific company (Super Admin only)
   * @param companyId - The ID of the company
   */
  async removeAdminListingUrlSync(companyId: string): Promise<{
    success: boolean
    message: string
  } | null> {
    return withErrorHandling(
      async () => {
        const response = await this.request<
          ApiResponse<{
            success: boolean
            message: string
          }>
        >(`${API_ROUTES.ADMIN.LISTINGS.SYNC_URL}?companyId=${companyId}`, {
          method: 'DELETE',
        })
        return response.data || null
      },
      {
        onApiError: 'log-and-rethrow',
        onOtherError: 'log-and-return-null',
        errorContext: 'Error removing listing URL sync for company',
      },
    )
  }
}

// Export a singleton instance
export const knowledgeApiClient = new KnowledgeApiClient()
